import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HiCheckCircle, HiArrowLeft } from 'react-icons/hi';
import { formatAmount } from '../utils/format';

export default function PaymentSuccess() {
  const [searchParams]  = useSearchParams();
  const navigate        = useNavigate();
  const sessionId        = searchParams.get('session_id');
  const freelancerUserId = searchParams.get('freelancer_user_id');

  const [details,  setDetails]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    if (!sessionId) { setError('No session ID found'); setLoading(false); return; }

    const verify = async () => {
      try {
        const res  = await fetch(`/api/payments/verify/${sessionId}?freelancer_user_id=${freelancerUserId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Verification failed');
        setDetails(data);
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    };

    verify();
  }, [sessionId, freelancerUserId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <svg className="animate-spin h-10 w-10 text-green-500" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">

      {/* Header */}
      <div className="flex items-center gap-2 mb-10">
        <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
          <span className="text-white font-bold text-sm">P</span>
        </div>
        <span className="font-bold text-gray-900 text-lg tracking-tight">PortalFlow</span>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 max-w-md w-full text-center">

        {/* Icon */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <HiCheckCircle className="w-12 h-12 text-green-500" />
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
        <p className="text-sm text-gray-500 mb-8">
          Your payment has been processed successfully. Thank you!
        </p>

        {error ? (
          <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-xl mb-6">
            Could not load payment details: {error}
          </div>
        ) : details && (
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-5 mb-8 text-left space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Invoice</span>
              <span className="font-semibold text-gray-900">{details.invoiceNumber}</span>
            </div>
            {details.clientName && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Client</span>
                <span className="font-semibold text-gray-900">{details.clientName}</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-3 border-t border-gray-200">
              <span className="text-sm font-bold text-gray-900">Amount Paid</span>
              <span className="text-xl font-bold text-green-600">{formatAmount(details.amount)}</span>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={() => navigate('/invoices')}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold
              py-3 rounded-xl transition text-sm"
          >
            View All Invoices
          </button>
          <button
            onClick={() => navigate(-1)}
            className="w-full flex items-center justify-center gap-2 text-sm text-gray-500
              hover:text-gray-700 font-medium py-2 transition"
          >
            <HiArrowLeft className="w-4 h-4" /> Go Back
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-400 mt-8">
        Powered by <span className="font-semibold text-indigo-600">PortalFlow</span> · Secured by Stripe
      </p>
    </div>
  );
}
